<script setup lang="ts">
import { api } from '@/api';
import {
  CreateOrUpdateProtocolDto,
  EntityWithCommentType,
  ProtocolDto,
  ReferencingObjectDto,
} from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { TabDefinition } from '@/components/Tabs/types.ts';
import { routenames } from '@/router/routenames';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import VeeProtocolPublicationDate from './VeeProtocolPublicationDate.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import { useDeleteProtocolEntityWithConfirmation } from '../utils';
import { useAmountComments } from '@/components/comments/useAmountComments';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { useFormTab } from '@/components/forms/useFormTab';

type Protocol = ProtocolDto & CreateOrUpdateProtocolDto;

const { t } = useTranslation();
const router = useRouter();
const values = useFormValues<Protocol>();
const mode = useFormMode();

const { commentsChanged, commentsTabTitle } = useAmountComments(values);
const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        indicatorState: getIndicatorState([
          'name',
          'dateOfPublication',
          'publisher',
          'attachments',
        ]),
      },

      comments: {
        ...defaultPageTabs.comments,
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
      },
      history: {
        ...defaultPageTabs.history,
        hidden: defaultPageTabs.history.hidden || mode.value === 'add',
        unmountTab: true,
      },
    }) satisfies TabDefinition,
);

const { action: deleteAction } = useDeleteProtocolEntityWithConfirmation();

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);

  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.protocols.edit,
        params: { id: values.value.id },
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                ...(values.value as EntityInfo),
                typeName: 'Protocol_one',
              },
              t,
            ),
          });
          if (result?.success) {
            router.push({ name: routenames.protocols.overview });
          }
        }
      },
    },
  ];
});
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <InlineNotification
        v-if="values.isReferenced && mode === 'edit'"
        variant="flat"
        class="mb-4"
        :value="{
          type: 'info',
          message: $t('EntityReferencesInfoMsg', {
            entity: $t('Protocol_one'),
          }),
        }"
      >
        <template #append>
          <span
            v-if="values.isReferenced"
            class="ml-2 linkButton"
            @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
            >{{ $t('Info') }}</span
          >
        </template>
      </InlineNotification>
      <v-row class="justify-center justify-sm-space-between">
        <v-col cols="10">
          <v-row>
            <v-col cols="4">
              <VeeTextField :label="$t('Name')" name="name" :placeholder="$t('Required')" />
              <VeeProtocolPublicationDate
                name="dateOfPublication"
                :label="$t('DateOfPublication')"
                class="gutterTop"
              />
            </v-col>

            <v-col cols="4">
              <VeeTextarea
                name="description"
                :label="$t('Description')"
                :useMarkdown="true"
                rows="7"
              />
              <VeeAttachments
                class="mt-1"
                name="attachments"
                data-testid="Description_Attachments"
              ></VeeAttachments>
            </v-col>

            <v-col cols="4">
              <VeeTextField
                :label="$t('PublisherAuthor')"
                name="publisher"
                :placeholder="$t('Required')"
              />
              <VeeTextarea
                :label="$t('References')"
                name="references"
                :useMarkdown="true"
                class="gutterTop"
                rows="1"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="auto">
          <VeeAvailability :disabled="mode !== 'details'" name="isAvailable" />
          <EntityChangeBlock v-if="mode === 'details'" :entity="values" />
        </v-col>
      </v-row>
    </template>

    <template #comments>
      <Comments
        v-if="values.id"
        :id="values.id"
        :entityWithCommentType="EntityWithCommentType.Protocol"
        @change="commentsChanged"
      />
    </template>

    <template #history>
      <HistoryEvents
        v-if="values.id"
        :id="values.id"
        :historyApiFn="api.protocolsApi.getProtocolHistory.bind(api.protocolsApi)"
      />
    </template>
  </BasePageNavigation>
  <EntityReferencesModal
    v-if="values.isReferenced"
    :references="entityReferences"
    :title="$t('ReferenceingEntities')"
    @dialogOpen="
      (isOpen: boolean) => {
        if (!isOpen) entityReferences = null;
      }
    "
  ></EntityReferencesModal>
</template>
